/**
 * Supabase Track Repository
 * Handles insight tracking via Supabase
 */

import type {
  ITrackRepository,
  TrackedInsight,
} from '../interfaces/ITrackRepository'
import { requireSupabaseClient } from '@/lib/supabase'

interface TrackRow {
  id: string
  user_id: string
  insight_id: string
  tracked_at: string
  price_at_track: number | null
}

interface TrackWithInsight extends TrackRow {
  insights: {
    id: string
    content: string
    ticker: string | null
    sentiment: 'bullish' | 'bearish' | 'neutral'
    created_at: string
    image_url: string | null
    tags: string[] | null
    profiles: {
      username: string
      display_name: string | null
    } | null
  } | null
}

export class SupabaseTrackRepository implements ITrackRepository {
  /**
   * Track an insight
   */
  async trackInsight(insightId: string, priceAtTrack?: number): Promise<void> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { error } = await supabase.from('insight_tracks').insert({
      user_id: user.id,
      insight_id: insightId,
      price_at_track: priceAtTrack || null,
    } as Record<string, unknown>)

    if (error) {
      // Ignore duplicate key error (already tracking)
      if (error.code === '23505') return
      throw new Error(`Failed to track insight: ${error.message}`)
    }
  }

  /**
   * Untrack an insight
   */
  async untrackInsight(insightId: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { error } = await supabase
      .from('insight_tracks')
      .delete()
      .eq('user_id', user.id)
      .eq('insight_id', insightId)

    if (error) {
      throw new Error(`Failed to untrack insight: ${error.message}`)
    }
  }

  /**
   * Check if user is tracking an insight
   */
  async isTracking(insightId: string): Promise<boolean> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return false

    const { data, error } = await supabase
      .from('insight_tracks')
      .select('id')
      .eq('user_id', user.id)
      .eq('insight_id', insightId)
      .single()

    if (error && error.code !== 'PGRST116') {
      throw new Error(`Failed to check tracking: ${error.message}`)
    }

    return !!data
  }

  /**
   * Get all tracked insights for current user
   */
  async getTrackedInsights(): Promise<TrackedInsight[]> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return []

    const { data, error } = await supabase
      .from('insight_tracks')
      .select(
        `
        *,
        insights:insight_id (
          id,
          content,
          ticker,
          sentiment,
          created_at,
          image_url,
          tags,
          profiles:user_id (
            username,
            display_name
          )
        )
      `
      )
      .eq('user_id', user.id)
      .order('tracked_at', { ascending: false })
      .returns<TrackWithInsight[]>()

    if (error) {
      throw new Error(`Failed to get tracked insights: ${error.message}`)
    }

    return (data || [])
      .filter((row) => row.insights) // Filter out any with deleted insights
      .map((row) => ({
        id: row.id,
        insightId: row.insight_id,
        userId: row.user_id,
        trackedAt: new Date(row.tracked_at),
        priceAtTrack: row.price_at_track,
        insight: {
          id: row.insights!.id,
          content: row.insights!.content,
          ticker: row.insights!.ticker,
          sentiment: row.insights!.sentiment,
          username: row.insights!.profiles?.username || 'unknown',
          displayName: row.insights!.profiles?.display_name || undefined,
          createdAt: new Date(row.insights!.created_at),
          imageUrl: row.insights!.image_url || undefined,
          tags: row.insights!.tags || undefined,
        },
      }))
  }

  /**
   * Get tracking count for an insight
   */
  async getTrackCount(insightId: string): Promise<number> {
    const supabase = requireSupabaseClient()

    const { count, error } = await supabase
      .from('insight_tracks')
      .select('*', { count: 'exact', head: true })
      .eq('insight_id', insightId)

    if (error) {
      throw new Error(`Failed to get track count: ${error.message}`)
    }

    return count || 0
  }

  /**
   * Get user's tracked insight IDs (for bulk checking)
   */
  async getTrackedInsightIds(): Promise<string[]> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) return []

    const { data, error } = await supabase
      .from('insight_tracks')
      .select('insight_id')
      .eq('user_id', user.id)

    if (error) {
      throw new Error(`Failed to get tracked IDs: ${error.message}`)
    }

    return (data || []).map((row) => row.insight_id)
  }

  /**
   * Update the entry price for a tracked insight
   */
  async updateEntryPrice(
    insightId: string,
    priceAtTrack: number
  ): Promise<void> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('insight_tracks')
      .update({ price_at_track: priceAtTrack } as Record<string, unknown>)
      .eq('user_id', user.id)
      .eq('insight_id', insightId)
      .select()

    if (error) {
      throw new Error(`Failed to update entry price: ${error.message}`)
    }

    if (!data || data.length === 0) {
      throw new Error('Track record not found')
    }
  }
}
